import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import { debounce } from 'lodash'
import Constants from 'Constants'

const { GOOGLE_MAPS_API_KEY } = Constants

const fields = 'routes.polyline,routes.viewport,routes.legs'
const ROUTES_API_ENDPOINT = 'https://routes.googleapis.com/directions/v2:computeRoutes'

// Module-level cache to persist across component re-renders and remounts
const directionsCache = new Map()

/**
 * Hook to fetch directions from Google Routes API with caching
 */
const useCachedDirections = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [encodedPath, setEncodedPath] = useState(null)
  const latestRequestRef = useRef(0)

  const fetchDirections = useCallback(async (routeRequest, requestId) => {
    const cacheKey = JSON.stringify(routeRequest)
    if (directionsCache.has(cacheKey)) {
      if (requestId === latestRequestRef.current) {
        setEncodedPath(directionsCache.get(cacheKey))
        setIsLoading(false)
      }
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(ROUTES_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
          'X-Goog-FieldMask': fields,
        },
        body: JSON.stringify(routeRequest),
      })

      if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status}`)
      }

      const data = await response.json()
      const polyline = data.routes?.[0]?.polyline?.encodedPolyline

      if (requestId === latestRequestRef.current) {
        if (polyline) {
          directionsCache.set(cacheKey, polyline)
          setEncodedPath(polyline)
        } else {
          setEncodedPath(null)
        }
      }
    } catch (err) {
      console.error('Failed to fetch directions:', err)
      if (requestId === latestRequestRef.current) {
        setError(err.message)
        setEncodedPath(null)
      }
    } finally {
      if (requestId === latestRequestRef.current) {
        setIsLoading(false)
      }
    }
  }, [])

  const debouncedFetchDirections = useMemo(() => debounce(fetchDirections, 1000), [fetchDirections])



  const getDirections = useCallback(
    stops => {
      const requestId = ++latestRequestRef.current

      if (!stops || stops.length < 2) {
        setEncodedPath(null)
        setIsLoading(false)
        debouncedFetchDirections.cancel()
        return
      }

      // Extract lat/lng and format for API
      const points = stops
        .filter(s => s.latitude && s.longitude)
        .map(s => ({
          lat: parseFloat(s.latitude),
          lng: parseFloat(s.longitude),
        }))

      if (points.length < 2) {
        setEncodedPath(null)
        setIsLoading(false)
        debouncedFetchDirections.cancel()
        return
      }

      const origin = points[0]
      const destination = points[points.length - 1]
      const intermediates = points.slice(1, -1).map(p => ({
        location: { latLng: { latitude: p.lat, longitude: p.lng } },
      }))

      const routeRequest = {
        origin: { location: { latLng: { latitude: origin.lat, longitude: origin.lng } } },
        destination: { location: { latLng: { latitude: destination.lat, longitude: destination.lng } } },
        travelMode: 'DRIVE',
        routingPreference: 'TRAFFIC_UNAWARE',
        computeAlternativeRoutes: false,
        languageCode: 'en-US',
        units: 'IMPERIAL',
      }

      if (intermediates.length > 0) {
        routeRequest.intermediates = intermediates
      }

      const cacheKey = JSON.stringify(routeRequest)
      if (directionsCache.has(cacheKey)) {
        setEncodedPath(directionsCache.get(cacheKey))
        setIsLoading(false)
        debouncedFetchDirections.cancel()
        return
      }

      setIsLoading(true)
      debouncedFetchDirections(routeRequest, requestId)
    },
    [debouncedFetchDirections]
  )

  useEffect(() => {
    return () => {
      debouncedFetchDirections.cancel()
    }
  }, [debouncedFetchDirections])

  return { getDirections, encodedPath, isLoading, error }
}

export default useCachedDirections
